import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { IDS } from '@/constants/testIds';
import { LogOut, Trash2, Send, Save, ExternalLink } from 'lucide-react';

const TABS = [
  { id: 'bookings', label: 'Заявки', testid: IDS.adminTabBookings },
  { id: 'tours', label: 'Туры', testid: IDS.adminTabTours },
  { id: 'settings', label: 'Настройки', testid: IDS.adminTabSettings },
];

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState('bookings');
  const [bookings, setBookings] = useState([]);
  const [tours, setTours] = useState([]);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    if (admin === null) nav('/admin/login');
  }, [admin, nav]);

  useEffect(() => {
    if (!admin) return;
    if (tab === 'bookings') api.get('/admin/bookings').then((r) => setBookings(r.data)).catch(() => {});
    if (tab === 'tours') api.get('/admin/tours').then((r) => setTours(r.data)).catch(() => {});
    if (tab === 'settings') api.get('/admin/settings').then((r) => setSettings(r.data)).catch(() => {});
  }, [tab, admin]);

  if (admin === undefined) return <div className="min-h-screen flex items-center justify-center">…</div>;
  if (admin === null) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Панель администратора | Ali India Tours</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <header className="border-b border-border/60 sticky top-0 z-30 bg-background/95 backdrop-blur-md">
        <div className="container-x flex items-center justify-between py-5">
          <Link to="/" className="font-serif-display italic text-2xl">Ali<span className="text-saffron">.</span>Admin</Link>
          <div className="flex items-center gap-6">
            <span className="text-xs uppercase tracking-widest2 text-foreground/60 hidden md:inline">{admin?.email}</span>
            <button
              data-testid={IDS.adminLogout}
              onClick={logout}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-widest2 border border-border/60 px-4 py-2 hover:border-saffron hover:text-saffron transition-colors"
            >
              <LogOut size={14} /> Выйти
            </button>
          </div>
        </div>
        <div className="container-x flex gap-8 pb-3">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={t.testid}
              onClick={() => setTab(t.id)}
              className={`text-xs uppercase tracking-widest2 font-bold pb-2 border-b-2 transition-colors ${tab === t.id ? 'border-saffron text-saffron' : 'border-transparent text-foreground/60 hover:text-foreground'}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="container-x py-10">
        {tab === 'bookings' && <BookingsTab bookings={bookings} refresh={() => api.get('/admin/bookings').then((r) => setBookings(r.data))} />}
        {tab === 'tours' && <ToursTab tours={tours} refresh={() => api.get('/admin/tours').then((r) => setTours(r.data))} />}
        {tab === 'settings' && <SettingsTab settings={settings} setSettings={setSettings} />}
      </main>
    </div>
  );
}

// ==================== BOOKINGS ====================
function BookingsTab({ bookings, refresh }) {
  const updateStatus = async (id, status) => {
    try {
      await api.put(`/admin/bookings/${id}?status=${status}`);
      toast.success('Статус обновлён');
      refresh();
    } catch { toast.error('Ошибка'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Удалить заявку?')) return;
    await api.delete(`/admin/bookings/${id}`);
    refresh();
  };

  const statusColor = { new: 'text-saffron', contacted: 'text-blue-400', confirmed: 'text-green-500', cancelled: 'text-foreground/40' };

  return (
    <div>
      <h2 className="font-serif-display text-4xl mb-8">Заявки на туры <span className="text-foreground/40">({bookings.length})</span></h2>
      {bookings.length === 0 ? (
        <p className="text-foreground/60">Пока нет заявок.</p>
      ) : (
        <div className="space-y-4">
          {bookings.map((b) => (
            <div key={b.id} className="border border-border p-6 grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
              <div className="md:col-span-3">
                <p className="text-xs uppercase tracking-widest2 text-foreground/50">{new Date(b.created_at).toLocaleString('ru-RU')}</p>
                <p className="font-serif-display text-2xl">{b.name}</p>
                <p className="text-foreground/70 text-sm">{b.phone}{b.email ? ` · ${b.email}` : ''}</p>
              </div>
              <div className="md:col-span-4">
                <p className="text-xs uppercase tracking-widest2 text-foreground/50 mb-1">Тур</p>
                <p className="text-foreground/90">{b.tour_title}</p>
                <p className="text-foreground/60 text-sm">Гостей: {b.guests}{b.preferred_date ? ` · Дата: ${b.preferred_date}` : ''}</p>
                {b.message && <p className="mt-2 text-foreground/70 text-sm italic">«{b.message}»</p>}
              </div>
              <div className="md:col-span-3">
                <select
                  value={b.status}
                  onChange={(e) => updateStatus(b.id, e.target.value)}
                  className={`w-full bg-transparent border border-border/60 px-3 py-2 text-sm uppercase tracking-widest2 ${statusColor[b.status]}`}
                >
                  <option value="new">Новая</option>
                  <option value="contacted">Связались</option>
                  <option value="confirmed">Подтверждена</option>
                  <option value="cancelled">Отменена</option>
                </select>
                <p className="text-[10px] uppercase tracking-widest2 mt-2 text-foreground/40">
                  Telegram: {b.telegram_sent ? '✓ отправлено' : '— не отправлено'}
                </p>
              </div>
              <div className="md:col-span-2 flex flex-col gap-2 items-end">
                <a href={`https://wa.me/${b.phone.replace(/[^\d]/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-xs uppercase tracking-widest2 text-saffron hover:opacity-70">WhatsApp ↗</a>
                <button onClick={() => remove(b.id)} className="text-xs uppercase tracking-widest2 text-foreground/50 hover:text-crimson inline-flex items-center gap-1"><Trash2 size={12} /> Удалить</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== TOURS ====================
function ToursTab({ tours, refresh }) {
  const [editing, setEditing] = useState(null);
  const remove = async (id) => {
    if (!window.confirm('Удалить тур навсегда?')) return;
    await api.delete(`/admin/tours/${id}`);
    toast.success('Удалено');
    refresh();
  };

  return (
    <div>
      <h2 className="font-serif-display text-4xl mb-8">Туры <span className="text-foreground/40">({tours.length})</span></h2>
      <p className="text-foreground/60 text-sm mb-6">Полный CRUD доступен через API. Здесь — быстрое редактирование ключевых полей.</p>
      <div className="space-y-3">
        {tours.map((t) => (
          <div key={t.id} className="border border-border p-5 flex flex-col md:flex-row md:items-center gap-4">
            <img src={t.hero_image} alt="" className="w-full md:w-24 aspect-video md:aspect-square object-cover" />
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-widest2 text-foreground/50">{t.category} · {t.duration_days} дн.</p>
              <p className="font-serif-display text-xl truncate">{t.title}</p>
              <p className="text-foreground/60 text-xs truncate">{t.cities?.join(' · ')}</p>
            </div>
            <div className="flex flex-col md:items-end gap-2">
              <span className="font-serif-display text-2xl text-saffron">${t.price_usd}</span>
              <div className="flex gap-3">
                <Link to={`/tour/${t.slug}`} target="_blank" className="text-xs uppercase tracking-widest2 hover:text-saffron inline-flex items-center gap-1"><ExternalLink size={12} /> Открыть</Link>
                <button onClick={() => setEditing(t)} className="text-xs uppercase tracking-widest2 hover:text-saffron">Редактировать</button>
                <button onClick={() => remove(t.id)} className="text-xs uppercase tracking-widest2 text-foreground/50 hover:text-crimson"><Trash2 size={12} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <TourEditor tour={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />
      )}
    </div>
  );
}

function TourEditor({ tour, onClose, onSaved }) {
  const [t, setT] = useState({ ...tour });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        slug: t.slug, title: t.title, subtitle: t.subtitle || '',
        category: t.category, festival: t.festival || null,
        duration_days: +t.duration_days, duration_nights: +t.duration_nights,
        price_usd: +t.price_usd,
        cities: t.cities || [], hero_image: t.hero_image, gallery: t.gallery || [],
        next_dates: t.next_dates || [], summary: t.summary || '',
        highlights: t.highlights || [], program: t.program || [],
        included: t.included || [], not_included: t.not_included || [],
        important_info: t.important_info || [],
        is_active: t.is_active !== false, order: t.order || 0,
      };
      await api.put(`/admin/tours/${tour.id}`, payload);
      toast.success('Сохранено');
      onSaved();
    } catch (e) {
      toast.error('Ошибка сохранения');
    } finally { setBusy(false); }
  };

  const input = 'w-full bg-transparent border border-border/60 px-3 py-2 focus:border-saffron transition-colors text-foreground';

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-6">
      <div className="bg-background border border-border w-full max-w-3xl my-10 p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-serif-display text-3xl">Редактирование тура</h3>
          <button onClick={onClose} className="text-foreground/60 hover:text-crimson">✕</button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <label className="col-span-2"><span className="text-xs uppercase tracking-widest2 text-foreground/50">Название</span>
            <input className={input} value={t.title} onChange={(e) => setT({ ...t, title: e.target.value })} /></label>
          <label className="col-span-2"><span className="text-xs uppercase tracking-widest2 text-foreground/50">Подзаголовок</span>
            <input className={input} value={t.subtitle} onChange={(e) => setT({ ...t, subtitle: e.target.value })} /></label>
          <label><span className="text-xs uppercase tracking-widest2 text-foreground/50">Slug</span>
            <input className={input} value={t.slug} onChange={(e) => setT({ ...t, slug: e.target.value })} /></label>
          <label><span className="text-xs uppercase tracking-widest2 text-foreground/50">Категория</span>
            <select className={input} value={t.category} onChange={(e) => setT({ ...t, category: e.target.value })}>
              <option value="festival">Праздничный</option>
              <option value="weekly">Еженедельный</option>
              <option value="girls">Девичник 8 марта</option>
            </select></label>
          <label><span className="text-xs uppercase tracking-widest2 text-foreground/50">Цена USD</span>
            <input className={input} type="number" value={t.price_usd} onChange={(e) => setT({ ...t, price_usd: +e.target.value })} /></label>
          <label><span className="text-xs uppercase tracking-widest2 text-foreground/50">Дней</span>
            <input className={input} type="number" value={t.duration_days} onChange={(e) => setT({ ...t, duration_days: +e.target.value })} /></label>
          <label className="col-span-2"><span className="text-xs uppercase tracking-widest2 text-foreground/50">Даты (через запятую)</span>
            <input className={input} value={(t.next_dates || []).join(', ')} onChange={(e) => setT({ ...t, next_dates: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} /></label>
          <label className="col-span-2"><span className="text-xs uppercase tracking-widest2 text-foreground/50">Города (через запятую)</span>
            <input className={input} value={(t.cities || []).join(', ')} onChange={(e) => setT({ ...t, cities: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} /></label>
          <label className="col-span-2"><span className="text-xs uppercase tracking-widest2 text-foreground/50">Краткое описание</span>
            <textarea rows="3" className={input} value={t.summary} onChange={(e) => setT({ ...t, summary: e.target.value })} /></label>
          <label className="col-span-2"><span className="text-xs uppercase tracking-widest2 text-foreground/50">Обложка URL</span>
            <input className={input} value={t.hero_image} onChange={(e) => setT({ ...t, hero_image: e.target.value })} /></label>
        </div>
        <div className="flex justify-end gap-3 mt-8">
          <button onClick={onClose} className="px-6 py-3 border border-border/60 text-xs uppercase tracking-widest2 hover:border-crimson hover:text-crimson">Отмена</button>
          <button onClick={save} disabled={busy} className="px-6 py-3 border border-saffron bg-saffron text-foreground text-xs uppercase tracking-widest2 font-bold hover:opacity-90 inline-flex items-center gap-2">
            <Save size={14} /> {busy ? 'Сохраняем…' : 'Сохранить'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ==================== SETTINGS ====================
function SettingsTab({ settings, setSettings }) {
  const [busy, setBusy] = useState(false);
  if (!settings) return <p className="text-foreground/60">Загружаем…</p>;
  const s = settings;
  const set = (patch) => setSettings({ ...s, ...patch });
  const input = 'w-full bg-transparent border border-border/60 px-3 py-2 focus:border-saffron transition-colors text-foreground';

  const save = async () => {
    setBusy(true);
    try {
      await api.put('/admin/settings', s);
      toast.success('Настройки сохранены');
    } catch { toast.error('Ошибка сохранения'); }
    finally { setBusy(false); }
  };

  const testTg = async () => {
    setBusy(true);
    try {
      // Save first
      await api.put('/admin/settings', s);
      const r = await api.post('/admin/telegram/test');
      if (r.data.ok) toast.success('Тестовое сообщение отправлено в Telegram!');
      else toast.error('Telegram не отвечает. Проверьте токен и chat_id.');
    } catch { toast.error('Ошибка теста'); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl">
      <h2 className="font-serif-display text-4xl mb-8">Настройки</h2>

      <div className="border border-border p-6 mb-6">
        <p className="overline mb-4">Telegram-уведомления</p>
        <p className="text-foreground/60 text-sm mb-4">
          Создайте бота через <a href="https://t.me/BotFather" target="_blank" rel="noopener noreferrer" className="text-saffron hover-underline">@BotFather</a>, получите <b>токен</b>. Затем напишите своему боту любое сообщение и откройте {`https://api.telegram.org/bot<ТОКЕН>/getUpdates`} — там будет ваш <b>chat_id</b>.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <label><span className="text-xs uppercase tracking-widest2 text-foreground/50">Bot Token</span>
            <input className={input} value={s.telegram_bot_token || ''} onChange={(e) => set({ telegram_bot_token: e.target.value })} placeholder="123456:ABC…" /></label>
          <label><span className="text-xs uppercase tracking-widest2 text-foreground/50">Chat ID</span>
            <input className={input} value={s.telegram_chat_id || ''} onChange={(e) => set({ telegram_chat_id: e.target.value })} placeholder="123456789" /></label>
        </div>
        <button
          data-testid={IDS.adminTestTelegramBtn}
          onClick={testTg}
          disabled={busy}
          className="inline-flex items-center gap-2 px-5 py-3 border border-saffron text-saffron text-xs uppercase tracking-widest2 font-bold hover:bg-saffron hover:text-foreground"
        >
          <Send size={14} /> Отправить тест
        </button>
      </div>

      <div className="border border-border p-6 mb-6">
        <p className="overline mb-4">Публичные контакты</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label><span className="text-xs uppercase tracking-widest2 text-foreground/50">WhatsApp телефон</span>
            <input className={input} value={s.whatsapp_phone || ''} onChange={(e) => set({ whatsapp_phone: e.target.value })} /></label>
          <label><span className="text-xs uppercase tracking-widest2 text-foreground/50">Telegram (@username или номер)</span>
            <input className={input} value={s.telegram_username || ''} onChange={(e) => set({ telegram_username: e.target.value })} /></label>
          <label><span className="text-xs uppercase tracking-widest2 text-foreground/50">Email</span>
            <input className={input} value={s.email || ''} onChange={(e) => set({ email: e.target.value })} /></label>
          <label><span className="text-xs uppercase tracking-widest2 text-foreground/50">Instagram URL</span>
            <input className={input} value={s.instagram_url || ''} onChange={(e) => set({ instagram_url: e.target.value })} /></label>
          <label className="md:col-span-2"><span className="text-xs uppercase tracking-widest2 text-foreground/50">VK URL</span>
            <input className={input} value={s.vk_url || ''} onChange={(e) => set({ vk_url: e.target.value })} /></label>
        </div>
      </div>

      <button
        data-testid={IDS.adminSaveSettingsBtn}
        onClick={save}
        disabled={busy}
        className="px-8 py-4 border border-saffron bg-saffron text-foreground text-xs uppercase tracking-widest2 font-bold hover:opacity-90 inline-flex items-center gap-2"
      >
        <Save size={14} /> Сохранить настройки
      </button>
    </div>
  );
}
